import React, { useMemo, useRef } from 'react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@rocco/components/accordion';
import {
    parseFooterContentInBrowser,
    type ParsedFooterFAQContent,
} from '~/modules/professionals-category/util/parse-footer-content-in-browser';
import { replaceEmDashes } from '~/modules/professionals-category/util/replace-em-dashes';

/**
 * Interface representing a single FAQ element with title, question and answer
 * @interface FAQElement
 * @property {string} content - The full content of the FAQ
 * @property {string} title - The title/heading of the FAQ
 * @property {string} question - The FAQ question text
 * @property {string} answer - The FAQ answer text
 */
interface FAQElement {
    content: string;
    title: string;
    question?: string;
    answer?: string;
}

/**
 * Props interface for the CategoryPageFAQ component
 * @interface CategoryPageFAQProps
 * @property {FAQElement[]} faqSchemas - Array of FAQ items to display
 * @property {string} footerContent - HTML content to display in the header/footer section
 * @property {'accordion' | 'grid'} [variant='accordion'] - Display variant
 * @property {string} [categoryLabel] - Category label for grid variant header
 * @property {string} [seoHeading] - Visible h2 for the category SEO copy block
 * @property {ParsedFooterFAQContent} [parsedFooterContent] - Server-parsed footer for grid SSR
 */
interface CategoryPageFAQProps {
    faqSchemas: FAQElement[];
    footerContent: string;
    variant?: 'accordion' | 'grid';
    categoryLabel?: string;
    seoHeading?: string;
    parsedFooterContent?: ParsedFooterFAQContent;
}

/**
 * Grid variant component for static FAQ display (used on products pages)
 * Parses footerContent HTML to extract intro text and FAQ items
 * Displays FAQs in a newspaper-style 3-column layout
 */
const FAQGridVariant = ({
    footerContent,
    categoryLabel,
    seoHeading,
    parsedFooterContent,
}: {
    footerContent: string;
    categoryLabel?: string;
    seoHeading?: string;
    parsedFooterContent?: ParsedFooterFAQContent;
}) => {
    const clientParsedContent = useMemo(
        () => parseFooterContentInBrowser(footerContent),
        [footerContent],
    );
    const parsedContent = parsedFooterContent ?? clientParsedContent;

    const introHtml = replaceEmDashes(parsedContent.introHtml);
    const faqItems = parsedContent.faqItems.map((faq) => ({
        title: replaceEmDashes(faq.title),
        content: replaceEmDashes(faq.content),
    }));
    const cleanedFooterContent = replaceEmDashes(footerContent);
    const showRawFallback =
        !introHtml && faqItems.length === 0 && Boolean(footerContent);

    if (
        !introHtml &&
        faqItems.length === 0 &&
        !categoryLabel &&
        !showRawFallback
    ) {
        return null;
    }

    if (showRawFallback) {
        return (
            <div className="flex flex-col gap-14.5 px-6 text-black md:px-12">
                {seoHeading && (
                    <h2 className="text-6 font-medium tracking-[0.025rem] text-black">
                        {seoHeading}
                    </h2>
                )}
                {categoryLabel && (
                    <div className="border-t border-gray-200 pt-4">
                        <span className="text-4 font-normal tracking-[-0.03em] text-black">
                            {categoryLabel}
                        </span>
                    </div>
                )}
                <div
                    className="faq-footer"
                    dangerouslySetInnerHTML={{ __html: cleanedFooterContent }}
                />
                <div className="border-t border-gray-200 pt-4" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-14.5 px-6 text-black md:px-12">
            {seoHeading && (
                <h2 className="text-6 font-medium tracking-[0.025rem] text-black">
                    {seoHeading}
                </h2>
            )}
            {/* Header Section - category label and hero intro */}
            {(categoryLabel || introHtml) && (
                <div className="flex flex-col gap-11">
                    {categoryLabel && (
                        <div className="border-t border-gray-200 pt-4">
                            <span className="text-4 font-normal tracking-[-0.03em] text-black">
                                {categoryLabel}
                            </span>
                        </div>
                    )}
                    {introHtml && (
                        <div
                            className="faq-grid-intro font-twk-ghost text-[2.15rem] font-normal leading-[1.4] tracking-[-0.025em] text-black md:max-w-370"
                            dangerouslySetInnerHTML={{ __html: introHtml }}
                        />
                    )}
                </div>
            )}

            {/* FAQ Columns - newspaper-style layout flowing down then across */}
            {faqItems.length > 0 && (
                <div className="columns-1 gap-21.5 md:max-w-370 md:columns-3">
                    {faqItems.map((faq, index) => (
                        <div
                            key={`faq-grid-${index}`}
                            className="mb-10 flex break-inside-avoid flex-col gap-1.5 md:max-w-109"
                        >
                            <h3 className="text-3.5 font-semibold leading-normal tracking-[0.02em] text-black">
                                {faq.title}
                            </h3>
                            {faq.content && (
                                <div
                                    className="faq-grid-answer text-3.5 font-normal leading-[1.4] tracking-[0.02em] text-black"
                                    dangerouslySetInnerHTML={{
                                        __html: faq.content,
                                    }}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
            <div className="border-t border-gray-200 pt-4" />
        </div>
    );
};

/**
 * Component that renders an FAQ section with expandable accordion items
 * or as a static grid layout depending on the variant prop
 * @param {CategoryPageFAQProps} props - Component props
 * @returns {JSX.Element | null} The rendered FAQ section or null if no content
 */
export const CategoryPageFAQ = (props: CategoryPageFAQProps) => {
    const {
        faqSchemas,
        footerContent,
        variant = 'accordion',
        categoryLabel,
        seoHeading,
        parsedFooterContent,
    } = props;
    const openedItemRef = useRef<HTMLDivElement>(null);

    // For accordion variant, filter faqSchemas
    const filteredFAQSchemas = React.useMemo(
        () => faqSchemas.filter((faq) => faq.question && faq.answer),
        [faqSchemas],
    );

    // Grid variant - parses footerContent to extract FAQ items
    if (variant === 'grid') {
        if (!footerContent) {
            return null;
        }
        return (
            <FAQGridVariant
                footerContent={footerContent}
                categoryLabel={categoryLabel}
                seoHeading={seoHeading}
                parsedFooterContent={parsedFooterContent}
            />
        );
    }

    // Early return if no content for accordion variant
    if (filteredFAQSchemas.length === 0 && !footerContent) {
        return null;
    }

    // Default accordion variant (existing behavior)
    const defaultOpen = footerContent
        ? ['footerContent']
        : filteredFAQSchemas[0]?.question
          ? [filteredFAQSchemas[0]?.question]
          : [];

    return (
        <div className="px-6 text-black md:mb-16 md:px-12">
            <div className="flex flex-col pb-14.5 md:gap-12 md:pb-0">
                {seoHeading && footerContent && (
                    <h2 className="pt-6 text-6 font-medium tracking-[0.025rem] text-black md:pt-0">
                        {seoHeading}
                    </h2>
                )}
                {/* Footer content */}
                {footerContent && (
                    <div
                        className="faq-footer"
                        dangerouslySetInnerHTML={{
                            __html: replaceEmDashes(footerContent),
                        }}
                    />
                )}

                {/* Render FAQs only if present */}
                {filteredFAQSchemas.length > 0 && (
                    <div>
                        <h4 className="faq-footer-title">FAQs</h4>
                        <Accordion type="multiple" defaultValue={defaultOpen}>
                            {filteredFAQSchemas.map((faq) => (
                                <AccordionItem
                                    key={faq.title}
                                    value={faq.question || ''}
                                >
                                    <div ref={openedItemRef}>
                                        <AccordionTrigger
                                            variant="narrow"
                                            icon="plus"
                                            openIcon="minus"
                                        >
                                            <div className="flex w-full items-center justify-between">
                                                <span
                                                    className="text-left text-xl font-medium"
                                                    dangerouslySetInnerHTML={{
                                                        __html: faq.title,
                                                    }}
                                                />
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div
                                                className="grid py-2"
                                                dangerouslySetInnerHTML={{
                                                    __html: faq.answer || '',
                                                }}
                                            />
                                        </AccordionContent>
                                    </div>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                )}
            </div>
            <div className="border-t border-gray-200 pt-4" />
        </div>
    );
};
